import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { ActionText, Text } from "@mdotm/shadcn/legacy/components";
import { useRefProxy, type NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import { type ForwardedRef, useMemo } from "react";
import { renderToString } from "react-dom/server";
import { categoricalChartColor, dimmedSourceOpacity } from "./categorical-palette";
import {
	ChartCardShell,
	ChartTitle,
	chartHeightInPx,
	ChartWallSwitch,
	type ChartHeight,
	type ChartWallStatus,
} from "./ChartCardShell";

/**
 * Line: time on the X axis, one line per portfolio.
 *
 * The one chart kind whose X axis is not portfolios, which is why it shares only `ChartCardShell` with
 * the other three: there is no sorting (the axis is chronological), no threshold to drag, and no
 * band-drag selection — a band drawn over time would select a date range, not a set of portfolios.
 *
 * Colour is per *portfolio* and comes from the caller's order, so a portfolio keeps its colour between
 * its own two sources and across re-renders. CURRENT is solid and PROPOSAL is the same colour dashed,
 * `linkedTo` so the legend lists each portfolio once — the same convention the stacked column uses for
 * its two stacks.
 *
 * Points arrive in milliseconds: the wire carries `[epochSeconds, value]` and the mapper converts, so
 * nothing here multiplies.
 */

export type PerformanceLineChartItem = {
	id: string;
	name: string;
	/** `[CURRENT, PROPOSAL]` in `[timestampMs, value]` pairs. `null` means "no line for that source". */
	series: [Array<[number, number]> | null, Array<[number, number]> | null];
};

export type PerformanceLineChartProps = {
	innerRef?: ForwardedRef<HighchartsReactRefObject>;
	chartTitle: string;
	mainMetricLabel: string;
	secondaryMetricLabel: string;
	data: Array<PerformanceLineChartItem>;
	/** @default false */
	hasProposal?: boolean;
	/**
	 * Dims the solid ({@link mainMetricLabel}) lines so the dashed proposal ones read as the primary series.
	 * The legend keeps its full-strength swatch — it names the *portfolio*, which owns both lines.
	 */
	highlightSecondaryMetric?: boolean;
	/** @default true */
	showLegend?: boolean;
	/** e.g. % — the unit of the plotted value, i.e. of the Y axis. */
	unit?: string;
	midActions?: NodeOrFn;
	actions?: NodeOrFn;
	/** @default 'tall' */
	chartHeight?: ChartHeight;
	tooltipTitleHrefProvider?(itemId: string): string;
};

export function PerformanceLineChart(props: PerformanceLineChartProps): React.JSX.Element {
	const highchartsRef = useRefProxy(null, props.innerRef);
	const unit = props.unit ?? "";
	const { formatNumber, formatDate } = useLocaleFormatters();
	const hasProposal = props.hasProposal ?? false;

	/**
	 * Memoized because `containerProps` is a dependency of the layout effect that calls `chart.update`, so an
	 * object literal here re-inits every series on every render however stable the options object is.
	 */
	const containerProps = useMemo(
		() => ({ style: { height: chartHeightInPx(props.chartHeight ?? "tall"), width: "100%" } }),
		[props.chartHeight],
	);

	const chartOptions = useMemo((): Highcharts.Options => {
		// Nothing to highlight *against* without the dashed lines, so the dimming waits for them.
		const dimCurrent = (props.highlightSecondaryMetric ?? false) && hasProposal;

		const series = props.data.flatMap((item, index): Array<Highcharts.SeriesLineOptions> => {
			const color = categoricalChartColor(index);
			const lines: Array<Highcharts.SeriesLineOptions> = [];

			// A portfolio with no CURRENT line still has to occupy its colour slot and its legend entry, or
			// the proposal line below would be linked to the previous portfolio's series.
			lines.push({
				type: "line",
				id: `${item.id}-current`,
				name: item.name,
				color,
				// Dimmed per series, not per portfolio: only the solid line steps back, and its dashed twin
				// below keeps full strength.
				opacity: dimCurrent && item.series[1] ? dimmedSourceOpacity : undefined,
				custom: { itemId: item.id, source: props.mainMetricLabel },
				data: item.series[0] ?? [],
			});

			if (hasProposal && item.series[1]) {
				lines.push({
					type: "line",
					name: item.name,
					color,
					dashStyle: "Dash",
					// One legend entry per portfolio, not per portfolio × source: the dashed line is the same
					// portfolio seen a second time.
					linkedTo: `${item.id}-current`,
					custom: { itemId: item.id, source: props.secondaryMetricLabel },
					data: item.series[1],
				});
			}

			return lines;
		});

		return {
			// Drag-to-zoom on the time axis: with many portfolios the interesting window is usually a slice of
			// the requested horizon, and this is the cheapest way to get at it (`zoomType`, not `zooming` —
			// the latter is Highcharts 10+).
			chart: { type: "line", style: { fontFamily: "Gotham,sans-serif" }, zoomType: "x" },
			title: { text: "" },
			credits: { enabled: false },
			legend: {
				enabled: props.showLegend ?? true,
				align: "center",
				verticalAlign: "bottom",
				// A workspace can hold many portfolios, and a legend that grows without bound would eat the
				// plot. Highcharts paginates past this height instead.
				maxHeight: 64,
			},
			xAxis: {
				type: "datetime",
				title: { text: "" },
				tickWidth: 0,
			},
			yAxis: {
				gridLineDashStyle: "Dash",
				title: { text: "" },
				labels: {
					formatter(ctx) {
						return `${formatNumber(ctx.value, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`;
					},
				},
			},
			plotOptions: {
				line: {
					lineWidth: 1.5,
					// Daily points over years: a marker per point would be a solid band. Hover still gets one.
					marker: { enabled: false, states: { hover: { enabled: true } } },
					animation: { duration: 0 },
					shadow: false,
					// Highcharts refuses more than 1000 points per series unless this is off, and a 3-year daily
					// series is ~780 — close enough that the default would fail on the first longer window.
					turboThreshold: 0,
					// Hovering one portfolio dims the others rather than leaving a wall of lines equally loud.
					states: { inactive: { opacity: 0.2 } },
				},
			},
			tooltip: {
				useHTML: true,
				padding: 0,
				borderWidth: 0,
				shadow: true,
				stickOnContact: true,
				formatter(this) {
					const point = this.point;
					const seriesOptions = point.series.options as Highcharts.SeriesLineOptions;
					const meta = seriesOptions.custom as { itemId?: string; source?: string } | undefined;
					const color = seriesOptions.color as string | undefined;

					return renderToString(
						<div className="p-2 rounded-sm bg-white min-w-60 shadow-sm pointer-events-auto flex flex-col gap-2">
							<div className="py-1 rounded-sm bg-[#EFF0F3] text-center">
								{props.tooltipTitleHrefProvider && meta?.itemId ? (
									<ActionText href={props.tooltipTitleHrefProvider(meta.itemId)} type="Body/M/Bold">
										{point.series.name}
									</ActionText>
								) : (
									<Text type="Body/M/Bold">{point.series.name}</Text>
								)}
							</div>
							<div className="flex justify-between">
								<Text type="Body/S/Book">Date</Text>
								<Text type="Body/S/Bold">{formatDate(point.x)}</Text>
							</div>
							<div className="flex justify-between">
								<div className="flex items-center gap-1">
									<ColoredRectangle variant="vertical" color={color} />
									<Text type="Body/M/Book">{meta?.source ?? props.mainMetricLabel}</Text>
								</div>
								<Text type="Body/M/Bold">
									{`${formatNumber(point.y ?? 0, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`}
								</Text>
							</div>
						</div>,
					);
				},
			},
			series,
			navigator: { enabled: false },
			exporting: { enabled: false },
		};
	}, [props, unit, formatNumber, formatDate, hasProposal]);

	return (
		<ChartCardShell
			fullHeight={props.chartHeight === "full"}
			title={<ChartTitle>{props.chartTitle}</ChartTitle>}
			midActions={props.midActions}
			actions={props.actions}
		>
			<HighchartsReact
				containerProps={containerProps}
				highcharts={Highcharts}
				options={chartOptions}
				ref={highchartsRef}
			/>
		</ChartCardShell>
	);
}

export function PerformanceLineChartWall({
	status,
	...rest
}: PerformanceLineChartProps & { status: ChartWallStatus }): React.JSX.Element {
	return (
		<ChartWallSwitch
			status={status}
			chartTitle={rest.chartTitle}
			midActions={rest.midActions}
			actions={rest.actions}
			chartHeight={rest.chartHeight}
		>
			{() => <PerformanceLineChart {...rest} />}
		</ChartWallSwitch>
	);
}
